<template>
  <tab-item-template>
    <template #toolbar-items>
      <el-tooltip placement="bottom" content="プロセス手順生成" v-if="isEditable">
        <v-btn icon @click="openCreateDialog">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </el-tooltip>
    </template>

    <el-table
        :data="processOperationImplementationsView"
        border>
      <el-table-column
          prop="serviceComponentModelName"
          label="サービス実装">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.serviceComponentModelName"
                :value-local-id="row.serviceImplementation.localId">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          prop="processComponentModelName"
          label="プロセス実装">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.processComponentModelName"
                :value-local-id="row.processImplementation.localId">
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          v-for="pot in processOperationTypeItems"
          :key="pot.name"
          :label="pot.label"
          :prop="pot.name"
          width="50"
          align="center">
        <template #default="{row}">
          <table-inline>
            <readonly-checkbox :value="row.operationTypes.includes(pot.name)"></readonly-checkbox>
          </table-inline>
        </template>
      </el-table-column>

      <el-table-column
          width="95"
          align="center"
          fixed="right">
        <template #default="{row}">
          <icon-btns>
            <!-- 編集モード -->
            <template v-if="isEditable">
              <icon-btn
                  type="delete"
                  @click="openDeleteDialog(row)">
              </icon-btn>
              <icon-btn
                  type="edit-content"
                  @click="openUpdateDialog(row)">
              </icon-btn>
            </template>
          </icon-btns>
        </template>
      </el-table-column>
    </el-table>

    <!-- 作成ダイアログ -->
    <simple-dialog
        v-model="dialogCreate.visible"
        title-text="プロセス手順実装作成"
        @close="closeCreateDialog">
      <process-implementation-select-form
          v-model="dialogCreate.processImplementationId"
          :data-implementation="dataImplementation"
          :service-implementations="serviceImplementations"
          :process-implementations="processImplementations"
          @change="dialogCreate.operationTypes = dialogCreate.operationTypesInitial = collectOperationTypes($event)">
        <template #append="{processImplementation}">
          <v-row>
            <v-col cols="12">
              <process-operation-type-select-form
                  v-model="dialogCreate.operationTypes"
                  :process-component-model-id="processImplementation && processImplementation.processComponentModelId"
                  :data-component-model-id="dataImplementation.dataComponentModelId"
                  :disabled="!dialogCreate.processImplementationId">
              </process-operation-type-select-form>
            </v-col>
          </v-row>
        </template>
      </process-implementation-select-form>
    </simple-dialog>

    <!-- 編集ダイアログ -->
    <simple-dialog
        v-model="dialogUpdate.visible"
        title-text="プロセス手順実装編集"
        @close="closeUpdateDialog">
      <process-implementation-select-form
          v-model="dialogUpdate.processImplementationId"
          :data-implementation="dataImplementation"
          :service-implementations="serviceImplementations"
          :process-implementations="processImplementations"
          readonly>
        <template #append="{processImplementation}">
          <v-row>
            <v-col cols="12">
              <process-operation-type-select-form
                  v-model="dialogUpdate.operationTypes"
                  :process-component-model-id="processImplementation && processImplementation.processComponentModelId"
                  :data-component-model-id="dataImplementation.dataComponentModelId">
              </process-operation-type-select-form>
            </v-col>
          </v-row>
        </template>
      </process-implementation-select-form>
    </simple-dialog>

    <!-- 削除ダイアログ -->
    <simple-dialog
          v-model="dialogDelete.visible"
          title-text="プロセス手順実装削除"
          title-color="delete"
          apply-text="削除"
          header-text="以下のプロセス手順実装を削除します。本当によろしいですか？"
          @close="closeDeleteDialog">
      <process-implementation-select-form
          v-model="dialogDelete.processImplementationId"
          :data-implementation="dataImplementation"
          :service-implementations="serviceImplementations"
          :process-implementations="processImplementations"
          readonly>
        <template #append>
          <v-row>
            <v-col cols="12">
              <text-field-like
                  label="区分"
                  :value="
                    Object.entries(operationTypes)
                      .map(([name, label]) => dialogDelete.operationTypes.includes(name) && label)
                      .filter(label => !!label)
                      .join(', ')"
                  :messages="`${dialogDelete.operationTypes.length} 件`">
              </text-field-like>
            </v-col>
          </v-row>
        </template>
      </process-implementation-select-form>
    </simple-dialog>
  </tab-item-template>
</template>

<script>
import { mapMutations } from "vuex";
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import PathInfoMixin from "@/router/PathInfoMixin";
import SimpleDialog from "@/components/organisms/SimpleDialog.vue";
import { PROCESS_OPERATION_OPERATION_TYPES } from "@/constants";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import TableInline from "@/components/atoms/TableInline.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import ProcessImplementationSelectForm
  from "@/components/pages/implementation/DataImplementationShow/ProcessImplementationSelectForm";
import ProcessOperationTypeSelectForm
  from "@/components/pages/implementation/DataImplementationShow/ProcessOperationTypeSelectForm";
import BetterSet from "betterset";
import groupArray from "group-array";
import ReadonlyCheckbox from "@/components/atoms/ReadonlyCheckbox.vue";

export default {
  name: 'ProcessOperationImplementationTable',
  mixins: [
    PathInfoMixin
  ],
  components: {
    ReadonlyCheckbox,
    ProcessOperationTypeSelectForm,
    ProcessImplementationSelectForm,
    TextFieldLike,
    TableInline,
    IconBtn,
    IconBtns,
    TabItemTemplate,
    SimpleDialog,
  },
  props: {
    processOperationImplementations: {
      type: Array,
      required: true
    },
    dataImplementation: {
      type: Object,
      required: true
    },
    serviceImplementations: {
      type: Array,
      required: true
    },
    processImplementations: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      processOperationTypeItems: ['create', 'read', 'update', 'delete'].map(t => ({
        name: t,
        label: PROCESS_OPERATION_OPERATION_TYPES[t],
      })),
      dialogCreate: {
        visible: false,
        processImplementationId: null,
        operationTypesInitial: [],
        operationTypes: []
      },
      dialogUpdate: {
        visible: false,
        processImplementationId: null,
        operationTypesInitial: [],
        operationTypes: []
      },
      dialogDelete: {
        visible: false,
        processImplementationId: null,
        operationTypes: []
      },
    }
  },
  computed: {
    operationTypes: () => PROCESS_OPERATION_OPERATION_TYPES,
    processOperationImplementationsView() {
      return Object.values(groupArray(this.processOperationImplementations, poi => poi.processImplementationId)).map(group => {
        const first = group[0];
        if(first === undefined) {
          return undefined;
        }
        return {
          ...first,
          id: undefined,
          index: undefined,
          createdAt: undefined,
          updatedAt: undefined,
          operationType: undefined,
          operationTypes: this.collectOperationTypes(first.processImplementationId)
        };
      }).filter(poi => !!poi);
    }
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    openCreateDialog() {
      this.dialogCreate = {
        visible: true,
        processImplementationId: null,
        operationTypesInitial: [],
        operationTypes: []
      };
    },
    async closeCreateDialog(apply) {
      this.dialogCreate.visible = false;
      if(apply) {
        this.showSnackbar({
          message: 'プロセス手順を更新中...',
          timeout: false,
          loading: true
        });

        try {
          await this.updateProcessOperationImplementations(this.dialogCreate);
          this.$store.commit({
            type: 'showSnackbar',
            message: `プロセス手順が更新されました`
          });
        } catch (e) {
          console.error(e);
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: `プロセス手順の更新に失敗しました`
          });
        }
      }
    },
    openUpdateDialog(row) {
      this.dialogUpdate = {
        visible: true,
        processImplementationId: row.processImplementationId,
        operationTypesInitial: row.operationTypes,
        operationTypes: row.operationTypes
      };
    },
    async closeUpdateDialog(apply) {
      this.dialogUpdate.visible = false
      if(apply) {
        this.showSnackbar({
          message: 'プロセス手順を更新中...',
          timeout: false,
          loading: true
        });

        try {
          await this.updateProcessOperationImplementations(this.dialogUpdate);
          this.$store.commit({
            type: 'showSnackbar',
            message: `プロセス手順が更新されました`
          });
        } catch (e) {
          console.error(e);
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: `プロセス手順の更新に失敗しました`
          });
        }
      }
    },
    openDeleteDialog(row) {
      this.dialogDelete = {
        visible: true,
        processImplementationId: row.processImplementationId,
        operationTypes: row.operationTypes
      };
    },
    async closeDeleteDialog(apply) {
      this.dialogDelete.visible = false;
      if(apply) {
        this.showSnackbar({
          message: 'プロセス手順を削除中...',
          timeout: false,
          loading: true
        });
        try {
          for(const operationType of this.dialogDelete.operationTypes) {
            const poi = this.processOperationImplementations.find(poi =>
                poi.processImplementationId === this.dialogDelete.processImplementationId &&
                poi.operationType === operationType);
            if(poi) {
              await this.$store.dispatch('hcs/processOperationImplementation/delete', poi.id);
            }
          }
          this.$store.commit({
            type: 'showSnackbar',
            message: `プロセス手順が削除されました`
          });
        } catch (e) {
          this.$store.commit({
            type: 'showSnackbar',
            color: 'failed',
            message: `プロセス手順の削除に失敗しました`
          });
        }
      }
    },
    /**
     * 指定されたプロセス実装IDに関連するプロセス手順実装の区分を収集します
     *
     * @param {string} processImplementationId プロセス実装ID
     * @return {(keyof typeof PROCESS_OPERATION_OPERATION_TYPES)[]} プロセス手順実装の区分の配列
     */
    collectOperationTypes(processImplementationId) {
      const result = new BetterSet();
      this.processOperationImplementations.forEach(poi => {
        if(poi.processImplementationId === processImplementationId) {
          result.add(poi.operationType);
        }
      });
      return result.asArray();
    },
    /**
     * 新旧のプロセス手順実装の区分の配列の差分を取得します
     *
     * @param {string[]} oldTypes 旧区分の配列
     * @param {string[]} newTypes 新区分の配列
     * @return {{create: string[], remove: string[]}} 配列の差分
     */
    diffOperationTypes(oldTypes, newTypes) {
      const oldTypeSet = new BetterSet(oldTypes);
      const newTypeSet = new BetterSet(newTypes);
      const intersection = oldTypeSet.intersection(newTypeSet);
      return {
        create: newTypeSet.relativeComplement(intersection).asArray(),
        remove: oldTypeSet.relativeComplement(intersection).asArray()
      };
    },
    /**+
     * プロセス手順実装を更新します
     *
     * @param {{
     *   processImplementationId: string,
     *   operationTypesInitial: (keyof typeof PROCESS_OPERATION_OPERATION_TYPES)[],
     *   operationTypes: (keyof typeof PROCESS_OPERATION_OPERATION_TYPES)[],
     * }} form 更新データ
     * @return {Promise<void>}
     */
    async updateProcessOperationImplementations(form) {
      const processImplementationId = form.processImplementationId;
      const otDiff = this.diffOperationTypes(form.operationTypesInitial, form.operationTypes);

      let currentIndex = this.processOperationImplementations
          .filter(poi => poi.processImplementationId === processImplementationId)
          .map(poi => poi.index)
          .reduce((a, b) => Math.max(a, b), 1);

      const removePois = otDiff.remove
          .flatMap(operationType => this.processOperationImplementations
              .filter(poi => poi.processImplementationId === processImplementationId && poi.operationType === operationType));

      const createPois = otDiff.create.map(operationType => ({
        processImplementationId,
        dataImplementationId: this.dataImplementation.id,
        index: ++currentIndex,
        operationType: operationType
      }));

      for(const removePoi of removePois) {
        await this.$store.dispatch('hcs/processOperationImplementation/delete', removePoi.id);
      }

      for(const createPoi of createPois) {
        await this.$store.dispatch('hcs/processOperationImplementation/create', createPoi);
      }
    },
  }
}
</script>
